<template>
  <div class="insights-page">
    <DashboardPageHeader
      title="Insights"
      :subtitle="`${displayName || 'Your business'} at a glance.`"
    />

    <div v-if="activationPending" class="activation-banner" role="status">
      <v-icon class="activation-icon">mdi-clock-outline</v-icon>
      <div>
        <strong>Your subscription is being activated.</strong>
        Metrics will populate as soon as your first scans come in - this
        usually takes just a few moments.
      </div>
    </div>

    <div v-if="loading" class="insights-loading">
      <BrandedLoader text="Loading insights…" spinner-color="primary" />
    </div>

    <div v-else-if="loadError" class="insights-error" role="alert">
      <v-icon class="insights-error-icon">mdi-alert-circle-outline</v-icon>
      <h2>We couldn't load your insights</h2>
      <p>{{ loadError }}</p>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-refresh"
        @click="fetchData"
      >
        Try again
      </v-btn>
    </div>

    <div v-else-if="snapshot === null" class="insights-empty">
      <v-icon class="insights-empty-icon">mdi-chart-bar-stacked</v-icon>
      <h2>This business doesn't have any data yet</h2>
      <p>Once your customers start scanning, insights will appear here.</p>
    </div>

    <template v-else>
      <div
        class="insights-body"
        :class="{ 'insights-body--refetching': refetching }"
        :aria-busy="refetching"
      >
        <KpiGrid
          :items="primaryKpiItems"
          :loading="false"
        />

        <div class="secondary-stats">
          <div v-for="stat in secondaryItems" :key="stat.id" class="secondary-stat">
            <div class="secondary-stat-label">{{ stat.label }}</div>
            <div class="secondary-stat-value">{{ stat.value }}</div>
          </div>
        </div>

        <section class="chart-grid">
          <div class="chart-tile chart-tile--wide">
            <BaseChart
              type="area"
              title="Scan activity over time"
              description="Daily card scans over the selected period - spot trends, busy spells and quiet stretches."
              :series="scanActivitySeries.series"
              :categories="scanActivitySeries.categories"
              :height="280"
              x-axis-title="Date"
              y-axis-title="Scans"
              empty-message="No scans recorded yet."
            >
              <template #header-actions>
                <v-btn-toggle
                  v-model="activityRange"
                  mandatory
                  divided
                  density="comfortable"
                  variant="outlined"
                  color="primary"
                >
                  <v-btn value="7">7d</v-btn>
                  <v-btn value="30">30d</v-btn>
                  <v-btn value="90">90d</v-btn>
                </v-btn-toggle>
              </template>
            </BaseChart>
          </div>

          <div class="chart-tile">
            <BaseChart
              type="bar"
              title="Scans vs redemptions (12 weeks)"
              description="Weekly stamps earned compared with rewards redeemed - see how earning turns into reward claims."
              :series="metrics.charts.scansVsRedemptions12Weeks.series"
              :categories="metrics.charts.scansVsRedemptions12Weeks.categories"
              :colors="['rgb(0, 109, 62)', '#ffffff']"
              :series-stroke-colors="['transparent', 'rgb(0, 109, 62)']"
              :height="280"
              x-axis-title="Week starting"
              y-axis-title="Count"
              empty-message="No scans or redemptions in the last 12 weeks."
            />
          </div>

          <div class="chart-tile">
            <BaseChart
              type="bar"
              title="New customer sign-ups (12 months)"
              description="How many new customers joined your rewards programme each month over the past year."
              :series="metrics.charts.newSignupsByMonth.series"
              :categories="metrics.charts.newSignupsByMonth.categories"
              :height="280"
              x-axis-title="Month"
              y-axis-title="New customers"
              empty-message="No new sign-ups in the last 12 months."
            />
          </div>

          <div class="chart-tile">
            <BaseChart
              type="bar"
              title="Busiest day of the week"
              description="Total scans by weekday - find your peak days to plan staffing and promotions."
              :series="metrics.charts.busiestDayOfWeek.series"
              :categories="metrics.charts.busiestDayOfWeek.categories"
              :height="260"
              x-axis-title="Day of week"
              y-axis-title="Scans"
              empty-message="No scans yet."
            />
          </div>

          <div class="chart-tile">
            <BaseChart
              type="bar"
              title="Busiest hour of the day"
              description="Total scans by hour - see when foot traffic peaks across the day."
              :series="metrics.charts.busiestHourOfDay.series"
              :categories="metrics.charts.busiestHourOfDay.categories"
              :height="260"
              x-axis-title="Hour of day"
              y-axis-title="Scans"
              empty-message="No scans yet."
            />
          </div>

          <div class="chart-tile">
            <BaseChart
              type="bar"
              title="Loyalty funnel"
              description="Customers at each stage, from first scan to repeat visits and redeemed rewards."
              :series="metrics.charts.loyaltyFunnel.series"
              :categories="metrics.charts.loyaltyFunnel.categories"
              :height="260"
              x-axis-title="Stage"
              y-axis-title="Customers"
              empty-message="No customer activity yet."
            />
          </div>

          <div class="chart-tile">
            <BaseChart
              type="donut"
              title="Customer engagement"
              description="Your customer base split by activity level - new, active and lapsed."
              :series="metrics.charts.customerEngagementSegments.series"
              :categories="metrics.charts.customerEngagementSegments.categories"
              :colors="['rgb(0, 79, 45)', 'rgb(76, 175, 80)', 'rgb(160, 210, 170)']"
              :height="280"
              empty-message="No customers yet."
            />
          </div>

          <div class="chart-tile chart-tile--wide">
            <BaseChart
              type="bar"
              title="Top customers"
              description="Your most loyal customers ranked by total stamps earned."
              :series="topCustomersHorizontal.series"
              :categories="topCustomersHorizontal.categories"
              :height="400"
              :horizontal="true"
              x-axis-title="Stamps"
              y-axis-title="Customer"
              empty-message="No stamps recorded yet."
            />
          </div>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from "vue";
import {
  getBusinessSnapshot,
  getBusinessDisplayName,
} from "@/firebase-business.js";
import {
  buildDashboardMetrics,
  scanActivityOverTime,
} from "@/businessMetrics.js";
import { track } from "@/analytics.js";
import BrandedLoader from "@/components/Molecules/BrandedLoader.vue";
import DashboardPageHeader from "@/components/Molecules/DashboardPageHeader.vue";
import KpiGrid from "@/components/Molecules/KpiGrid.vue";
import BaseChart from "@/components/Organisms/BaseChart.vue";

const SECONDARY_FORMATTERS = {
  percent: (n) => `${Math.round(n * 100)}%`,
  decimal: (n) => (Math.round(n * 10) / 10).toLocaleString(),
  integer: (n) => Math.round(n).toLocaleString(),
};

export default {
  name: "DashboardInsightsComponent",
  components: {
    BrandedLoader,
    DashboardPageHeader,
    KpiGrid,
    BaseChart,
  },
  setup() {
    const selectedBusinessId = inject("selectedBusinessId");
    // `loading` is the first-load splash; `refetching` is the inline state
    // shown when the user switches business (no full-page loader flash).
    const loading = ref(true);
    const refetching = ref(false);
    const loadError = ref("");
    const snapshot = ref(null);
    const displayName = ref("");
    const activityRange = ref("30");

    const metrics = computed(() => buildDashboardMetrics(snapshot.value || {}));

    const activationPending = computed(() => {
      const snap = snapshot.value;
      return snap != null && snap.isActive === false;
    });

    const fetchData = async () => {
      const id = selectedBusinessId?.value;
      if (!id) {
        snapshot.value = null;
        loading.value = false;
        refetching.value = false;
        return;
      }
      const isFirstLoad = snapshot.value == null && !loadError.value;
      if (isFirstLoad) loading.value = true;
      else refetching.value = true;
      loadError.value = "";
      try {
        const [snap, name] = await Promise.all([
          getBusinessSnapshot(id),
          getBusinessDisplayName(id),
        ]);
        snapshot.value = snap;
        displayName.value = name || "";
        track("dashboard_insights_viewed", {
          business_id: id,
          has_data: snap !== null,
        });
      } catch (e) {
        loadError.value =
          "Something went wrong loading your insights. Please try again in a moment.";
      } finally {
        loading.value = false;
        refetching.value = false;
      }
    };

    onMounted(fetchData);
    watch(() => selectedBusinessId?.value, fetchData);

    const primaryKpiItems = computed(() => {
      const k = metrics.value.kpis;
      return [
        { id: "scansToday", label: "Scans today", value: k.scansToday, icon: "mdi-qrcode-scan" },
        { id: "scansLast7Days", label: "Scans (7d)", value: k.scansLast7Days, icon: "mdi-calendar-week" },
        { id: "totalScans", label: "Total scans", value: k.totalScans, icon: "mdi-counter" },
        { id: "customersEnrolled", label: "Customers enrolled", value: k.customersEnrolled, icon: "mdi-account-group" },
        { id: "totalRedemptions", label: "Total redemptions", value: k.totalRedemptions, icon: "mdi-gift-outline" },
        { id: "dueToRedeemNow", label: "Due to redeem", value: k.dueToRedeemNow, icon: "mdi-star-outline" },
      ];
    });

    const secondaryItems = computed(() => {
      const s = metrics.value.secondary;
      return [
        { id: "repeat", label: "Repeat customer rate", value: SECONDARY_FORMATTERS.percent(s.repeatCustomerRate) },
        { id: "avg", label: "Avg stamps / customer", value: SECONDARY_FORMATTERS.decimal(s.avgStampsPerCustomer) },
        { id: "redemption", label: "Redemption rate", value: SECONDARY_FORMATTERS.percent(s.redemptionRate) },
        { id: "active30", label: "Active customers (30d)", value: SECONDARY_FORMATTERS.integer(s.activeCustomersLast30Days) },
      ];
    });

    const scanActivitySeries = computed(() => {
      const range = Number(activityRange.value);
      return scanActivityOverTime(snapshot.value?.stamps, range);
    });

    const topCustomersHorizontal = computed(() => {
      const t = metrics.value.charts.topCustomers;
      return {
        categories: t.categories,
        series: t.series,
      };
    });

    return {
      loading,
      refetching,
      loadError,
      snapshot,
      displayName,
      metrics,
      activationPending,
      activityRange,
      primaryKpiItems,
      secondaryItems,
      scanActivitySeries,
      topCustomersHorizontal,
      fetchData,
    };
  },
};
</script>

<style scoped>
.insights-page :deep(.v-btn) {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Standalone primary action buttons (Export data, Try again) - green pill.
   Excludes buttons inside v-btn-toggle so the segment control keeps its
   own visual grouping. */
.insights-page :deep(.v-btn--variant-flat.text-primary):not(.v-btn-toggle .v-btn),
.insights-page :deep(.v-btn.bg-primary):not(.v-btn-toggle .v-btn) {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
}

.insights-page :deep(.v-btn--variant-flat.text-primary):not(.v-btn-toggle .v-btn):hover,
.insights-page :deep(.v-btn.bg-primary):not(.v-btn-toggle .v-btn):hover {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

/* Segment control (7d/30d/90d) - keep its colour scheme but add the
   subtle pop on hover so behaviour matches the rest of the app. */
.insights-page :deep(.v-btn-toggle .v-btn:hover) {
  transform: translateY(-1px);
}

/* Disabled state must not lift. */
.insights-page :deep(.v-btn--disabled:hover) {
  transform: none !important;
}

.insights-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.activation-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--lulu-warning-soft);
  border: 1px solid var(--lulu-warning-border);
  border-radius: var(--lulu-radius-card);
  padding: 12px 16px;
  color: var(--lulu-warning-text);
  font-size: 13px;
  line-height: 1.5;
}

.activation-icon {
  color: var(--lulu-warning-strong) !important;
}

.insights-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: opacity 0.2s ease;
}

.insights-body--refetching {
  opacity: 0.55;
  pointer-events: none;
}

.insights-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.insights-empty {
  text-align: center;
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 48px 24px;
}

.insights-empty-icon {
  color: var(--lulu-text-subtle);
  font-size: 48px !important;
  margin-bottom: 12px;
}

.insights-error {
  text-align: center;
  background: var(--lulu-surface);
  border: 1px solid var(--lulu-danger-border);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 36px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.insights-error-icon {
  color: var(--lulu-danger) !important;
  font-size: 40px !important;
}

.insights-error h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--lulu-text-strong);
}

.insights-error p {
  margin: 0 0 4px;
  color: var(--lulu-text-muted);
  font-size: 14px;
}

.insights-empty h2 {
  margin: 4px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--lulu-text-strong);
}

.insights-empty p {
  margin: 0;
  color: var(--lulu-text-muted);
  font-size: 14px;
}

.secondary-stats {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, 1fr);
}

@media (min-width: 960px) {
  .secondary-stats {
    grid-template-columns: repeat(4, 1fr);
  }
}

.secondary-stat {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.secondary-stat-label {
  font-size: 12px;
  color: var(--lulu-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.secondary-stat-value {
  font-size: 22px;
  font-weight: 800;
  color: var(--lulu-text-strong);
}

.chart-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
}

@media (min-width: 720px) {
  .chart-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .chart-tile--wide {
    grid-column: span 2;
  }
}

@media (min-width: 960px) {
  .chart-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .chart-tile--wide {
    grid-column: span 2;
  }
}

.chart-tile {
  display: flex;
  flex-direction: column;
}
</style>
